import React, { useState, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';

import {
  eventTrack,
  VIDEO_COMPLETE,
  VIDEO_PLAY_PAUSE,
  VIDEO_PROGRESS,
} from 'utils/google-tag-manager';

import * as Styled from './Video.styled';

const PROGRESS_WAY_POINTS = [0, 25, 50, 75, 100];

export const TYPES = {
  YOUTUBE: 'YouTube',
  VIMEO: 'Vimeo',
  DEFAULT: 'Default',
} as const;

export type VIDEO_TYPES_ENUM = typeof TYPES[keyof typeof TYPES];

export interface VideoPlayerPropsType {
  /**
   * An optional Video Type otherwise defaults to DEFAULT
   */
  type: VIDEO_TYPES_ENUM | string;
  /**
   * Video Id
   */
  videoId: string;
  /**
   * Video Name
   */
  videoName: string;
  /**
   * An optional caption at the bottom of the video
   */
  caption?: string;
  /**
   * An optional number from 0-1 to set the volume
   */
  volume?: number;
  /**
   * The source of the video
   */
  url?: string;
  /**
   * Shows or hide video player controls
   */
  controls?: boolean;
  /**
   * mutes or unmutes audio
   */
  muted?: boolean;
  /**
   * Play/Pause the video
   */
  playing?: boolean;
  /**
   * Loop the video
   */
  loop?: boolean;
  /**
   * Enable auto-generated captions in the video
   */
  showCaptions?: boolean;
}

/**
 * VideoPlayer component
 */
const VideoPlayer = ({
  url,
  videoId,
  videoName,
  type,
  caption,
  playing: playingArg = false,
  controls,
  muted,
  volume,
  loop,
  showCaptions = false,
}: VideoPlayerPropsType) => {
  const [progress, setProgress] = useState(0);
  const [indexPoint, setIndexPoint] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasWindow, setHasWindow] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasWindow(true);
    }
  }, []);

  const taggingProgress = useCallback(
    ({ played }) => {
      const percentagePlayed = Math.floor(played * 100);
      const currentProgressPoint = PROGRESS_WAY_POINTS[indexPoint];

      setProgress(percentagePlayed);

      if (percentagePlayed >= currentProgressPoint && playing) {
        eventTrack(VIDEO_PROGRESS, {
          progress: `${currentProgressPoint}%`,
          videoName,
          videoUrl: url,
          videoPlayer: type,
          videoId,
          videoCaption: caption,
        });
        setIndexPoint(prevIndexPoint => prevIndexPoint + 1);
      }
    },
    [playing, indexPoint],
  );

  const taggingPlayPause = useCallback(
    isPlaying => {
      setPlaying(isPlaying);
      eventTrack(VIDEO_PLAY_PAUSE, {
        isPlaying,
        progress: `${progress}%`,
        videoName,
        videoUrl: url,
        videoPlayer: type,
        videoCaption: caption,
        videoId,
      });
    },
    [progress],
  );

  const taggingEnd = useCallback(() => {
    eventTrack(VIDEO_COMPLETE, {
      videoName,
      videoUrl: url,
      videoPlayer: type,
      videoId,
      videoCaption: caption,
    });
  }, [videoName, url, type, videoId, caption]);

  const handlePlay = () => taggingPlayPause(true);

  const handlePause = () => taggingPlayPause(false);

  useEffect(() => {
    if (playingArg) {
      handlePlay();
    } else {
      handlePause();
    }
  }, [playingArg]);

  const captionConfigs = showCaptions ? 'en-x-autogenerated' : '';
  const playerConfigs = {
    vimeo: { playerOptions: { texttrack: captionConfigs } },
  };

  return (
    <Styled.VideoContainer>
      <Styled.Video>
        {hasWindow && (
          <ReactPlayer
            url={url}
            width="100%"
            height="100%"
            controls={controls}
            volume={volume}
            muted={muted}
            onProgress={taggingProgress}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={taggingEnd}
            playing={playing}
            loop={loop}
            playsinline
            config={playerConfigs}
          />
        )}
      </Styled.Video>
    </Styled.VideoContainer>
  );
};

const VIDEO_TYPES = {
  [TYPES.YOUTUBE]: {
    VideoComponent: VideoPlayer,
    srcFormatter: (videoId: string) =>
      `https://www.youtube.com/embed/${videoId}`,
  },
  [TYPES.VIMEO]: {
    VideoComponent: VideoPlayer,
    srcFormatter: (videoId: string) =>
      `https://player.vimeo.com/video/${videoId}`,
  },
  [TYPES.DEFAULT]: {
    VideoComponent: VideoPlayer,
    srcFormatter: (url: string) => url,
  },
} as const;

/**
 * @typedef VideoData
 * @property {ReactComponent} VideoComponent - A component that handles the video render
 * @property {Function} srcFormatter - A function that handles the URL format for the video
 */

/**
 * getVideoByType - Gets the video data by type
 * @param {string} type - Video Type
 * @returns {VideoData}
 */
const getVideoByType = (type: VIDEO_TYPES_ENUM | string) =>
  // @ts-ignore
  VIDEO_TYPES[type] || VIDEO_TYPES[TYPES.DEFAULT];

export interface VideoPropsType {
  type: VIDEO_TYPES_ENUM | string;
  videoId: string;
  videoName: string;
  caption?: string;
  volume?: number;
  url?: string;
  controls?: boolean;
  muted?: boolean;
  playing?: boolean;
  loop?: boolean;
  showCaptions?: boolean;
}

/**
 * Video component
 */
const Video = ({
  type = 'Vimeo',
  videoId,
  videoName = '',
  caption = '',
  url,
  playing,
  controls = true,
  muted = false,
  volume = 1,
  loop = false,
  showCaptions,
}: VideoPropsType) => {
  const { VideoComponent, srcFormatter } = getVideoByType(type);
  return (
    <Styled.Root>
      <VideoComponent
        url={srcFormatter(videoId || url)}
        videoId={videoId}
        videoName={videoName}
        type={type}
        caption={caption}
        playing={playing}
        controls={controls}
        muted={muted}
        volume={volume}
        loop={loop}
        showCaptions={showCaptions}
      />
      {caption && <Styled.Caption>{caption}</Styled.Caption>}
    </Styled.Root>
  );
};

export default Video;
